import { Fragment, useEffect, useRef } from 'react';

import TrashCan from '~components/Icon/TrashCan';
import LoadingCircle from '~components/Loading';
import { CareerType } from '~features/company/SalaryInfo/types';
import useWikiSalaryDeleteMutation from '~hooks/query/company/useWikiSalaryDelete';
import useWikiSalaryList from '~hooks/query/company/useWikiSalaryList';
import useAdminStore from '~store/admin';
import { formattedMoney } from '~utils/formatted/number';

import { ModalSalaryInfoProps } from '../..';
import { useGetUserInfo, useInfiniteLoadingDetector } from './hooks';
import * as S from './style';

interface SalariesWiki {
  career: number;
  userName: string;
  salary: number;
  year: number;
}

interface Props extends Pick<ModalSalaryInfoProps, 'companyId'> {
  prefix: CareerType;
  category: string;
}

interface WikiSalariItemProps extends SalariesWiki, Pick<Props, 'prefix'> {
  wikiId: number;
  onDelete: () => void;
}

export default function WikiSalaryList({ prefix, category, companyId }: Props) {
  const { data, status, refetch, fetchNextPage, hasNextPage } = useWikiSalaryList(
    companyId,
    category,
  );
  const triggerRef = useRef<HTMLLIElement>(null);
  const isVisible = useInfiniteLoadingDetector({ ref: triggerRef });

  useEffect(() => {
    if (isVisible && hasNextPage && status !== 'loading') {
      fetchNextPage();
    }
  }, [isVisible, status, category]);

  return (
    <S.Wrapper>
      {status === 'loading' && (
        <S.InfoWrapper>
          <LoadingCircle />
        </S.InfoWrapper>
      )}
      {data?.pages.map((group, index) => (
        <Fragment key={index}>
          {group.count === 0 ? (
            <S.InfoWrapper>
              <S.NoData>연봉 제보 후 내 연봉랭킹 알아보기!</S.NoData>
            </S.InfoWrapper>
          ) : (
            <>
              {group.rows.map((wikiSalary) => (
                <WikiSalariItem
                  wikiId={wikiSalary.id}
                  key={`wiki-${wikiSalary.id}`}
                  career={wikiSalary.career}
                  userName={wikiSalary.userName}
                  year={wikiSalary.year}
                  salary={wikiSalary.salary}
                  prefix={prefix}
                  onDelete={() => refetch()}
                />
              ))}
            </>
          )}
        </Fragment>
      ))}
      <S.EntryPoint ref={triggerRef} />
    </S.Wrapper>
  );
}

function WikiSalariItem({
  wikiId,
  prefix,
  career,
  userName,
  year,
  salary,
  onDelete,
}: WikiSalariItemProps) {
  const { isAdmin } = useAdminStore();
  const { mutate } = useWikiSalaryDeleteMutation();
  const userInfo = useGetUserInfo(prefix, userName, career);

  const handleDelete = () => {
    if (isAdmin) {
      if (window.confirm('정말 삭제하시겠습니까?')) {
        mutate(
          { wikiId },
          {
            onSuccess: () => {
              alert('삭제완료!');
              if (onDelete) {
                onDelete();
              }
            },
          },
        );
      }
    }
  };

  return (
    <S.Item>
      <div>
        <S.UserInfo>{userInfo}</S.UserInfo>
        <S.Year>{year ? <>{year}년 기준</> : <>정보없음</>}</S.Year>
      </div>
      <S.Salary>{formattedMoney(salary)}만원</S.Salary>
      {isAdmin && (
        <S.ButtonDelete onClick={handleDelete}>
          <TrashCan />
        </S.ButtonDelete>
      )}
    </S.Item>
  );
}
